/* eslint-disable max-lines */
import { type PlacementPay } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  ALL_WEEKDAYS,
  ALL_WEEKEND_DAYS,
  type DayOfTheWeek,
  isDayOfWeek,
} from "@src/appV2/lib/dates/types";

import { jobTypeOptions, shiftTypeOptions } from "../../PlacementCandidate/constants";
import { isJobType, isShiftNameType, JobType, ShiftNameType } from "../../PlacementCandidate/types";
import { type JobShift } from "../types/fetchPlacements.schema";
import { type TransparencyPayRate } from "../types/payRate";

export interface EstimatedPaySearchCriteria {
  workplaceId: string;
  jobShifts: JobShift[];
}

export function isMatchingJobShift(jobShift: JobShift, payRate: TransparencyPayRate): boolean {
  return (
    payRate.jobTypes.includes(jobShift.jobType) &&
    jobShift.shiftTypes.some((shiftType) => payRate.shiftTypes.includes(shiftType)) &&
    jobShift.daysOfWeek.some((dayOfWeek) => payRate.daysOfWeek.includes(dayOfWeek))
  );
}

function zeroToUndefined(value: number | undefined) {
  return value === 0 ? undefined : value;
}

export function findMatchingPayRates(
  searchCriteria: EstimatedPaySearchCriteria,
  payRatesForWorkplace: PlacementPay[] | undefined
): TransparencyPayRate[] {
  if (!payRatesForWorkplace) {
    return [];
  }

  const matchingPayRates = payRatesForWorkplace
    .map<TransparencyPayRate>((matchingPayRate) => ({
      baseRate: zeroToUndefined(matchingPayRate.attributes.exactPayRate) ?? 0,
      benefitsDiff: zeroToUndefined(matchingPayRate.attributes.benefitsDiff),
      healthBenefitsDiff: zeroToUndefined(matchingPayRate.attributes.healthBenefitsDiff),
      ptoBenefitsDiff: zeroToUndefined(matchingPayRate.attributes.ptoBenefitsDiff),
      weekendDiff: zeroToUndefined(matchingPayRate.attributes.weekendDiff),
      shiftTypeDiff: {
        [ShiftNameType.AM]: zeroToUndefined(matchingPayRate.attributes.dayDiff),
        [ShiftNameType.PM]: zeroToUndefined(matchingPayRate.attributes.eveningDiff),
        [ShiftNameType.NOC]: zeroToUndefined(matchingPayRate.attributes.nightDiff),
      },
      minYearsOfExperience: matchingPayRate.attributes.minYearsOfExperience,
      maxYearsOfExperience: matchingPayRate.attributes.maxYearsOfExperience,
      jobTypes: matchingPayRate.attributes.jobTypes.filter(isJobType),
      shiftTypes: matchingPayRate.attributes.timeSlots.filter(isShiftNameType),
      daysOfWeek: matchingPayRate.attributes.daysOfWeek.filter(isDayOfWeek),
      workerTypes: matchingPayRate.attributes.qualifications,
    }))
    .filter((payRate) =>
      searchCriteria.jobShifts.some((jobShift) => isMatchingJobShift(jobShift, payRate))
    );

  if (matchingPayRates.length === 0) {
    return [];
  }

  return matchingPayRates;
}

export function computeMinMaxPay(
  payRates: TransparencyPayRate[],
  { yearsOfExperience, includeBenefits }: { yearsOfExperience?: number; includeBenefits?: boolean }
): { min: number; max: number } | undefined {
  let filteredPayRates = payRates;

  if (isDefined(yearsOfExperience)) {
    const sortedPayRates = payRates
      .filter((payRate) => payRate.maxYearsOfExperience >= yearsOfExperience)
      .sort(
        (a, b) =>
          Math.abs(a.minYearsOfExperience - yearsOfExperience) -
          Math.abs(b.minYearsOfExperience - yearsOfExperience)
      );

    // finds the first pay rate with a min years of experience that the user satisfies
    // payRates is already sorted by how close the years of experience is to the user's years of experience
    const closestYearsOfExperience =
      sortedPayRates
        .map((payRate) => payRate.minYearsOfExperience)
        .find((minYearsOfExperience) => minYearsOfExperience <= yearsOfExperience) ?? -1;

    filteredPayRates = payRates.filter(
      (payRate) => payRate.minYearsOfExperience === closestYearsOfExperience
    );
  }

  const allJobTypes = jobTypeOptions.filter((jobType) => jobType.value !== JobType.WEEKEND_WARRIOR);

  let min = -1;
  let max = -1;
  filteredPayRates.forEach((payRate) => {
    allJobTypes.forEach((jobType) => {
      shiftTypeOptions.forEach((shiftType) => {
        [true, false].forEach((isWeekendShift) => {
          [true, false].forEach((aIncludesBenefits) => {
            if (isDefined(includeBenefits) && aIncludesBenefits !== includeBenefits) {
              return;
            }

            if (
              !isMatchingJobShift(
                {
                  jobType: jobType.value,
                  shiftTypes: [shiftType.value],
                  daysOfWeek: isWeekendShift ? ALL_WEEKEND_DAYS : ALL_WEEKDAYS,
                },
                payRate
              )
            ) {
              return;
            }

            const overallPayBreakdown = getOverallPayBreakdown(payRate, {
              shiftTypes: [shiftType.value],
              benefits: {
                combined: aIncludesBenefits,
                health: aIncludesBenefits,
                pto: aIncludesBenefits,
              },
              isWeekendShift,
            });

            if (overallPayBreakdown.overallPay < min || min === -1) {
              min = overallPayBreakdown.overallPay;
            }

            if (overallPayBreakdown.overallPay > max || max === -1) {
              max = overallPayBreakdown.overallPay;
            }
          });
        });
      });
    });
  });

  if (min === -1 || max === -1) {
    return undefined;
  }

  return { min, max };
}

export interface BenefitsDifferential {
  combined: boolean;
  health: boolean;
  pto: boolean;
}

interface FindMatchingPayBreakdownParams {
  payRates: TransparencyPayRate[];
  jobType: JobType;
  shiftType: ShiftNameType;
  daysOfWeek: DayOfTheWeek[];
  benefits: BenefitsDifferential;
  yearsOfExperience: number;
}

export function getDefaultShiftTypeForDifferential(shiftTypes: ShiftNameType[]) {
  // choose the shift type the worker is most likely to get by prioritizing the most common shift types
  // see https://linear.app/clipboardhealth/issue/TF-1769/change-pay-database-schema-table-design
  return [ShiftNameType.AM, ShiftNameType.PM, ShiftNameType.NOC].find((shiftType) =>
    shiftTypes.includes(shiftType)
  );
}

function getShiftTypePayDifferential(payRate: TransparencyPayRate, shiftTypes?: ShiftNameType[]) {
  if (!shiftTypes || shiftTypes.length === 0) {
    return 0;
  }

  const chosenType = getDefaultShiftTypeForDifferential(shiftTypes);

  if (!chosenType) {
    return 0;
  }

  return payRate.shiftTypeDiff?.[chosenType] ?? 0;
}

// a rate which includes benefits is lower than a rate which does not include benefits
// (since the employer is paying for the benefits)
function getBenefitsPayDifferential(payRate: TransparencyPayRate, benefits: BenefitsDifferential) {
  const isCombined = isDefined(payRate.benefitsDiff);
  const combinedBenefitsDiff = isDefined(payRate.benefitsDiff) ? payRate.benefitsDiff : 0;
  const healthBenefitsDiff = isDefined(payRate.healthBenefitsDiff) ? payRate.healthBenefitsDiff : 0;
  const ptoBenefitsDiff = isDefined(payRate.ptoBenefitsDiff) ? payRate.ptoBenefitsDiff : 0;

  if (isCombined) {
    return benefits.combined ? 0 : combinedBenefitsDiff;
  }

  return (benefits.health ? 0 : healthBenefitsDiff) + (benefits.pto ? 0 : ptoBenefitsDiff);
}

function getWeekendPayDifferential(payRate: TransparencyPayRate, isWeekendShift: boolean) {
  return isWeekendShift ? (payRate.weekendDiff ?? 0) : 0;
}

export interface GetDifferentialJobTraits {
  shiftTypes?: ShiftNameType[];
  benefits: BenefitsDifferential;
  // note that this is not quite the same as a weekend_warrior
  // weekend_warrior means working back to back double shifts on the weekend
  // whereas this is just a shift on the weekend
  isWeekendShift: boolean;
}

function getOverallPayDifferential(
  payRate: TransparencyPayRate,
  jobTraits: GetDifferentialJobTraits
) {
  return (
    getShiftTypePayDifferential(payRate, jobTraits.shiftTypes) +
    getBenefitsPayDifferential(payRate, jobTraits.benefits) +
    getWeekendPayDifferential(payRate, jobTraits.isWeekendShift)
  );
}

function applyOverallDifferentialToPayRate(
  pay: number,
  payRate: TransparencyPayRate,
  jobTraits: GetDifferentialJobTraits
) {
  return pay + getOverallPayDifferential(payRate, jobTraits);
}

function getOverallPayBreakdown(
  payRate: TransparencyPayRate,
  differentialTrails: GetDifferentialJobTraits
): {
  overallPay: number;
  basePay: number;
  overallPayDifferential: number;
  shiftTypePayDifferential: number;
  benefitsPayDifferential: number;
  weekendPayDifferential: number;
  totalShiftTypeDifferential: number;
} {
  const overallPayDifferential = getOverallPayDifferential(payRate, differentialTrails);

  const shiftTypePayDifferential = getShiftTypePayDifferential(
    payRate,
    differentialTrails.shiftTypes
  );
  const benefitsPayDifferential = getBenefitsPayDifferential(payRate, differentialTrails.benefits);
  const weekendPayDifferential = getWeekendPayDifferential(
    payRate,
    differentialTrails.isWeekendShift
  );

  const totalShiftTypeDifferential = shiftTypePayDifferential + weekendPayDifferential;

  const basePayPreBenefit = payRate.baseRate;
  const basePay = basePayPreBenefit + benefitsPayDifferential;

  return {
    overallPay: applyOverallDifferentialToPayRate(basePayPreBenefit, payRate, differentialTrails),
    basePay,
    overallPayDifferential,
    shiftTypePayDifferential,
    benefitsPayDifferential,
    weekendPayDifferential,
    totalShiftTypeDifferential,
  };
}

export function findMatchingPayBreakdown(params: FindMatchingPayBreakdownParams) {
  const { payRates, jobType, shiftType, daysOfWeek, benefits, yearsOfExperience } = params;

  const sortedPayRates = payRates
    .filter((payRate) => payRate.maxYearsOfExperience >= yearsOfExperience)
    .sort((a, b) => b.minYearsOfExperience - a.minYearsOfExperience);

  const firstMatchingPayRate = sortedPayRates.find((payRate) => {
    const isMatchingYearsOfExperience = payRate.minYearsOfExperience <= yearsOfExperience;

    return (
      isMatchingYearsOfExperience &&
      isMatchingJobShift(
        {
          jobType,
          shiftTypes: [shiftType],
          daysOfWeek,
        },
        payRate
      )
    );
  });

  return firstMatchingPayRate
    ? getOverallPayBreakdown(firstMatchingPayRate, {
        shiftTypes: [shiftType],
        benefits,
        isWeekendShift: daysOfWeek.some((dayOfWeek) => ALL_WEEKEND_DAYS.includes(dayOfWeek)),
      })
    : undefined;
}
/* eslint-enable max-lines */
